import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Avatar, AvatarFallback, AvatarImage, Button, useGql } from '@ah/design';
import { MY_BLOCKED_USERS_Q, UNBLOCK_USER_M } from '../shared/queries.ts';
import type { BlockedHuman } from '../shared/types.ts';

/**
 * Settings section listing users the current user has blocked, with an
 * unblock action per row. Apple App Review checks that blocks are
 * reversible by the user — the unblock button satisfies that.
 *
 * Rendered as a self-contained section (header + card) so the host page
 * (`apps/mobile/src/routes/Settings.tsx`) just drops it in.
 */
export function BlockedUsersList() {
  const gql = useGql();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['moderation', 'myBlockedUsers'],
    queryFn: () => gql.request<{ myBlockedUsers: BlockedHuman[] }>(MY_BLOCKED_USERS_Q),
  });
  const unblockMutation = useMutation({
    mutationFn: (humanId: string) =>
      gql.request(UNBLOCK_USER_M, { humanId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['moderation', 'myBlockedUsers'] });
    },
  });

  const blocked = data?.myBlockedUsers ?? [];
  if (isLoading) return null;
  if (blocked.length === 0) return null;

  return (
    <section className="mt-6">
      <h2 className="px-4 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Blocked
      </h2>
      <ul className="border-y border-border bg-card divide-y divide-border">
        {blocked.map((b) => (
          <li key={b.id} className="flex items-center gap-3 px-4 py-3">
            <Avatar className="size-10 shrink-0">
              {b.pictureUrl && <AvatarImage src={b.pictureUrl} alt={b.name ?? ''} />}
              <AvatarFallback className="text-xs">
                {(b.name ?? '?').slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{b.name ?? 'Unknown'}</p>
              {b.headline && (
                <p className="text-xs text-muted-foreground truncate">{b.headline}</p>
              )}
            </div>
            <Button
              size="sm"
              variant="outline"
              disabled={unblockMutation.isPending}
              onClick={() => unblockMutation.mutate(b.id)}
            >
              Unblock
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}
